import { isPeerSiteBuyerAlias } from '../../utils/customerPlaceLabel';
import { resolveConfiguredZoneLabel } from '../customerLabels/customerFacingControlLabel';
import type { OverviewControlTopPattern } from './overviewTypes';

/**
 * Intentional area aliases only (real places under a selected site).
 * Peer-site nicknames (Warehouse #2 / Warehouse Room#1) were removed — use
 * registry area names via resolveCustomerPlaceLabel instead.
 */
const BUYER_ZONE_NAMES: Record<string, string> = {
  demo5tes: 'Restricted Forklift Zone',
};

/** Explicit unresolved state — never fall back to raw camera_id. */
export const CAMERA_NAME_UNAVAILABLE = 'Camera name unavailable';

export function resolveBuyerZoneDisplayName(zoneId: string | null | undefined): string {
  const id = String(zoneId || '').trim();
  if (!id) return 'Monitored zone';
  const configured = resolveConfiguredZoneLabel(id);
  if (configured) return configured;
  const mapped = BUYER_ZONE_NAMES[id.toLowerCase()];
  if (mapped && !isPeerSiteBuyerAlias(mapped)) return mapped;
  if (/^demo5tes$/i.test(id)) return 'Restricted Forklift Zone';
  return 'Monitored zone';
}

/**
 * True when a catalog/placement name is safe to show to customers.
 * Rejects raw ids, cam_* tokens, and any string containing "demo".
 */
export function isCustomerFacingCameraName(
  name: string | null | undefined,
  cameraId?: string | null,
): boolean {
  const label = String(name || '').trim();
  if (!label) return false;
  const id = String(cameraId || '').trim();
  if (id && label.toLowerCase() === id.toLowerCase()) return false;
  if (/^cam[_-]/i.test(label)) return false;
  if (/demo/i.test(label)) return false;
  return true;
}

/**
 * Resolve a customer-facing camera display name.
 * Prefer known buyer aliases, then a safe catalog/placement name.
 * Never returns the raw camera_id.
 */
export function resolveBuyerCameraDisplayName(
  cameraId: string | null | undefined,
  catalogName?: string | null,
): string {
  const id = String(cameraId || '').trim();
  if (!id) return CAMERA_NAME_UNAVAILABLE;
  // Peer-site buyer nicknames are not camera display names — prefer catalog
  // or unavailable. Registry area labels come from resolveCustomerPlaceLabel.
  const catalog = String(catalogName || '').trim();
  if (
    isCustomerFacingCameraName(catalog, id)
    && !isPeerSiteBuyerAlias(catalog)
  ) {
    return catalog;
  }
  return CAMERA_NAME_UNAVAILABLE;
}

/**
 * Scoped camera copy for Overview facts / open work / thumb fallbacks.
 * Resolved → "Camera <name>"; unresolved → CAMERA_NAME_UNAVAILABLE (no raw id).
 */
export function formatCustomerCameraLabel(
  cameraId: string | null | undefined,
  catalogName?: string | null,
): string {
  const resolved = resolveBuyerCameraDisplayName(cameraId, catalogName);
  if (resolved === CAMERA_NAME_UNAVAILABLE) return CAMERA_NAME_UNAVAILABLE;
  if (/^camera\b/i.test(resolved)) return resolved;
  return `Camera ${resolved}`;
}

export function formatZoneIdSecondary(zoneId: string | null | undefined): string | null {
  const id = String(zoneId || '').trim();
  if (!id) return null;
  return `Zone ID: ${id}`;
}

export function buyerPatternDisplayLabel(
  pattern: Pick<OverviewControlTopPattern, 'patternLabel' | 'incidentType'> | null | undefined,
): string {
  const type = String(pattern?.incidentType || '').trim().toLowerCase();
  // Prefer canonical incident type so zone suffixes on pattern_label cannot mislabel.
  if (
    type === 'near_miss'
    || type === 'forklift_proximity'
    || type === 'forklift_near_miss'
    || type === 'pedestrian_near_moving_forklift'
  ) {
    return 'Near Miss';
  }
  if (
    type === 'zone_violation'
    || type === 'zone_breach'
    || type === 'zone_intrusion'
    || type === 'danger_zone_entry'
    || type === 'danger_zone_observation'
  ) {
    return 'Restricted Zone Entry';
  }

  const raw = String(pattern?.patternLabel || pattern?.incidentType || '').trim();
  // Strip " · zoneId" suffixes before heuristic matching.
  const labelOnly = raw.replace(/\s*[·|].*$/, '').trim();
  const normalized = labelOnly.toLowerCase();
  if (
    normalized.includes('near miss')
    || normalized.includes('near_miss')
    || normalized.includes('near-miss')
    || normalized.includes('forklift proximity')
  ) {
    return 'Near Miss';
  }
  if (
    normalized.includes('zone violation')
    || normalized.includes('danger-zone entry')
    || normalized.includes('danger-zone observation')
    || normalized.includes('zone_violation')
    || normalized.includes('restricted zone')
  ) {
    return 'Restricted Zone Entry';
  }
  if (!raw) return 'Risk Pattern';
  if (labelOnly.includes('_')) {
    return labelOnly
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
  return labelOnly || raw;
}

export function recurringRiskPatternKpiTitle(count: number): string {
  if (count === 1) return 'Recurring hotspot requiring review';
  return 'Recurring hotspots requiring review';
}

export function parsePatternGroupContext(row: OverviewControlTopPattern | null | undefined): {
  zoneId: string;
  cameraId: string;
  deviceId: string;
  buyerZoneName: string;
} {
  const parts = String(row?.groupKey || '')
    .split('|')
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  const trailing = parts.length >= 4 ? parts.slice(-3) : [];
  const zoneId = String(row?.zoneId || trailing[0] || '').trim();
  const cameraId = String(trailing[1] || '').trim();
  const deviceId = String(trailing[2] || '').trim();
  return {
    zoneId,
    cameraId,
    deviceId,
    buyerZoneName: resolveBuyerZoneDisplayName(zoneId),
  };
}

export function formatOpenActionPrimaryLine(
  pattern: Pick<OverviewControlTopPattern, 'patternLabel' | 'incidentType' | 'zoneId' | 'groupKey'>,
  statusLabel: string,
): string {
  return `${buyerPatternDisplayLabel(pattern)} — ${statusLabel}`;
}
